/**
 * What a screen editing a configuration needs beyond the plan
 * (`planWithDescriptor`), from the descriptor alone: the fields locked for
 * the caller, the profiles recognised and applied, the reservation at the
 * environment's peak. The frontend's « Configuration » tab (step 8, part 2a,
 * 2026-10-21) reads them here, in place of its own Odoo-shaped copy of these
 * rules (retirée le 2026-10-23 ; ses cas restent figés par
 * `plugins/odoo/src/components/odooProjectForm/configurationEngine.test.ts`).
 */

import {
  withinBound,
  type AppTypeDescriptor,
  type ConfigurationGrant,
  type DescriptorComponent,
  type FieldValues,
  valueOf,
} from './descriptor';
import { evaluate, truthy } from './expression';
import { planWithDescriptor } from './plan';

/**
 * The configurable fields the caller may not change: for an editor, those
 * whose current value is beyond the editor's bounds — an administrator's
 * choice, refused by the plan's phase 2 (`locked`) —; none for an admin.
 */
export function lockedFieldIds(
  descriptor: AppTypeDescriptor,
  current: FieldValues,
  grant: ConfigurationGrant,
): Set<string> {
  if (grant === 'admin') {
    return new Set();
  }
  return new Set(
    descriptor.configurableFields
      .filter(field => {
        const value = valueOf(current, field.id);
        return value !== undefined && !withinBound(value, field.bounds.editor);
      })
      .map(field => field.id),
  );
}

/**
 * The first profile whose values `values` holds, or undefined. The `growOnly`
 * fields (the volumes) are left out of the match: `applyProfile` keeps a
 * volume larger than the profile's, the profile still applies. So are the
 * fields of `ignored` (hidden by their `when`, which a screen never sets).
 */
export function profileOf(
  descriptor: AppTypeDescriptor,
  values: FieldValues,
  ignored: ReadonlySet<string> = new Set(),
): string | undefined {
  return descriptor.profiles.find(profile =>
    Object.entries(profile.values).every(
      ([id, value]) =>
        ignored.has(id) ||
        descriptor.fieldById.get(id)?.growOnly === true ||
        valueOf(values, id) === value,
    ),
  )?.id;
}

/**
 * `draft` with the values of the profile `profileId`, as a creation form
 * writes them — but a `growOnly` field takes the larger of its CURRENT value
 * and the profile's (a volume is never shrunk), and a field of `locked`
 * keeps its draft value. An unknown profile leaves `draft` as it is.
 */
export function applyProfile(
  descriptor: AppTypeDescriptor,
  input: {
    draft: FieldValues;
    current: FieldValues;
    profileId: string;
    locked: ReadonlySet<string>;
  },
): FieldValues {
  const profile = descriptor.profiles.find(item => item.id === input.profileId);
  if (!profile) {
    return input.draft;
  }
  const next: FieldValues = { ...input.draft };
  for (const [id, value] of Object.entries(profile.values)) {
    if (input.locked.has(id)) {
      continue;
    }
    const before = valueOf(input.current, id);
    next[id] =
      descriptor.fieldById.get(id)?.growOnly === true &&
      typeof before === 'number' &&
      typeof value === 'number'
        ? Math.max(before, value)
        : value;
  }
  return next;
}

/** Millicores and Mi. */
export interface Reservation {
  cpu: number;
  memory: number;
}

/**
 * `value` if a whole number ≥ 0, else a French error — as the quota's
 * `count` (`environmentQuota.ts`): a figure the engine cannot read is said,
 * never counted as 0, which would understate the reservation.
 */
function wholeFigure(value: unknown, where: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    throw new Error(
      `Réservation : ${where} = ${JSON.stringify(
        value,
      )}, attendu un entier positif.`,
    );
  }
  return value;
}

/**
 * A component's request for `key`: a constant, or its field's value; 0 when
 * the component declares none (the LimitRange's default is the quota's
 * concern, not a reservation the configuration sizes).
 */
function request(
  component: DescriptorComponent,
  key: 'cpuRequest' | 'memoryRequest',
  values: FieldValues,
): number {
  const ref = component.resources[key];
  if (ref === undefined) {
    return 0;
  }
  return wholeFigure(
    typeof ref === 'string' ? valueOf(values, ref) : ref,
    `${component.id}.resources.${key}`,
  );
}

/**
 * What the environment reserves at its peak: every pod of every component
 * `enabled`, at its `pods` — Odoo's HPA maximum, Valkey, every PostgreSQL
 * instance. Over `values` as the plan would write them, its automatic fixes
 * applied (`planWithDescriptor` without a change: no adjustment fires, the
 * `autofix` requirements do), so that Valkey counts as soon as the
 * autoscaling asks for it. Throws, in French, on a `pods` or a request
 * that is not a whole number ≥ 0.
 */
export function peakReservation(
  descriptor: AppTypeDescriptor,
  values: FieldValues,
  grant: ConfigurationGrant,
): Reservation {
  const fixed = planWithDescriptor({
    descriptor,
    current: values,
    changes: {},
    grant,
  }).desired;
  const context = { values: fixed };
  let cpu = 0;
  let memory = 0;
  for (const component of descriptor.components) {
    if (component.enabled && !truthy(evaluate(component.enabled, context))) {
      continue;
    }
    const count = wholeFigure(
      evaluate(component.pods, context),
      `${component.id}.pods`,
    );
    cpu += count * request(component, 'cpuRequest', fixed);
    memory += count * request(component, 'memoryRequest', fixed);
  }
  return { cpu, memory };
}

/**
 * One pod of each component whose reservation the configuration sizes (its
 * `cpuRequest` and `memoryRequest` given by fields): what a capacity
 * profile reserves, as its card says it. A component of constant resources
 * (Valkey) is no profile's. Throws, in French, on a request that is not a
 * whole number ≥ 0 (a profile that leaves one of these fields out).
 */
export function sizedReservation(
  descriptor: AppTypeDescriptor,
  values: FieldValues,
): Reservation {
  let cpu = 0;
  let memory = 0;
  for (const component of descriptor.components) {
    if (typeof component.resources.cpuRequest === 'string') {
      cpu += request(component, 'cpuRequest', values);
    }
    if (typeof component.resources.memoryRequest === 'string') {
      memory += request(component, 'memoryRequest', values);
    }
  }
  return { cpu, memory };
}
